import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {RecordUtils} from "../PA.Core/Basic/Records";
import {
    IMessageProvider,
    IProgressProvider, MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import {
    IBusinessObject,
    IBusinessObjectChanges,
    IBusinessObjectChangesOptions,
    IBusinessObjectCreateLoadOptions
} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_EvtVolume, esWB_EvtVolumePR, esWB_MasterData_Set, esWB_SalesOrg,
} from "../PA.Core/PAdbSchema";
import {
    WB_EvtVolumeRow, WB_MasterDataSetRow, WB_SalesOrgRow, WB_EvtVolumePartitionRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbLESpend} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {Role_User_Admin} from "../PA.Core/Security/Roles";
import {EventVolumeBOValidations} from "./EventVolumeBO.Validations";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {BehaviorSubject} from "rxjs";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

export class EventVolumeBO
    implements
        IBusinessObject
{

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_EvtVolume: WB_EvtVolumeRow;

    _bSalesOrgEnabled: boolean
    _EvtVolumeEnterpriseSelectEnabledChanged: BehaviorSubject<{ SalesOrg: boolean}>

    constructor(options?: IBusinessObjectCreateLoadOptions) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Control Point'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_EvtVolume = null;

        // enabled after copy, disabled after save
        this._bSalesOrgEnabled = false;
        this._EvtVolumeEnterpriseSelectEnabledChanged = new BehaviorSubject<{SalesOrg: boolean}>(undefined);

        this.IHavePartitions_init()
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_EvtVolume(): WB_EvtVolumeRow {
        return this.drWB_EvtVolume;
    }

    get bSalesOrgEnabled(): boolean {
        return this._bSalesOrgEnabled;
    }

    get EvtVolumeEnterpriseSelectEnabledChanged(): BehaviorSubject<{ SalesOrg: boolean }> {
        return this._EvtVolumeEnterpriseSelectEnabledChanged;
    }

    async LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Control Point");
        let olc = self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for new Event Volume Control Point.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                let newEvtVolumeRow  = <WB_EvtVolumeRow> await this.dbContext.createRecordWithKey(esWB_EvtVolume)
                newEvtVolumeRow.setDefaults();
                self.drWB_EvtVolume = newEvtVolumeRow;
                self.dbContext.add(esWB_EvtVolume, newEvtVolumeRow);
                self.updateIdentity();
                await self.applyCreateOptions()


                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;

                self._OnLoadComplete = undefined;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();
        return olc;
    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Event Volume already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for Event Volume.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = EventVolumeBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = EventVolumeBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aEvtVolumeRow: any = recordResolver.get(esWB_EvtVolume, anId);

                if (_.isEmpty(aEvtVolumeRow)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }

                self.drWB_EvtVolume = aEvtVolumeRow;
                self.updateIdentity();

                if (_.isNumber(self.drWB_EvtVolume.cif.WB_MDSET_ID) && self.drWB_EvtVolume.cif.WB_MDSET_ID > 0) {
                    let mruService = new MDUsageService()
                    await mruService.saveMDSetUsage(self.drWB_EvtVolume.cif.WB_MDSET_ID)
                }

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    private updateIdentity(): void {

        const {
            WB_EVTVOLUME_ID,
            CONTROL_POINT_ID,
            CONTROL_POINT_NAME
        } = this.WB_EvtVolume.cif;
        this.Identity.setAll(WB_EVTVOLUME_ID, CONTROL_POINT_ID, CONTROL_POINT_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_EvtVolume, 'WB_EVTVOLUME_ID', 'CONTROL_POINT_ID', 'CONTROL_POINT_NAME');
        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId(Role_User_Admin)

        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_EvtVolume, undefined, 'CONTROL_POINT_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_EvtVolume, undefined, undefined)

    }

    async applyCreateOptions() {
        const self = this
        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_EvtVolume.cif.CONTROL_POINT_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_EvtVolume.WB_MasterDataSetRow = mds
                self.WB_EvtVolume.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_EvtVolume.cif.WB_SALESORG_ID = wbSalesOrg
            }
        }
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_EvtVolume.cif.CONTROL_POINT_NAME + '(' + self.WB_EvtVolume.cif.CONTROL_POINT_ID + ')'
        )

        self.ValidateOption = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Event Volume.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await EventVolumeBOValidations.ValidateFields(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const wbEvtVolume = <IwbLESpend>self.drWB_EvtVolume;
        this.SaveProgressProvider.start(
            'Saving ' + wbEvtVolume.CONTROL_POINT_NAME + '(' + wbEvtVolume.CONTROL_POINT_ID + ')'
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Control Point.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if(!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            await saveFunction(esWB_EvtVolume, self.drWB_EvtVolume, {})
            await saveFunction(esWB_EvtVolumePR, self.drWB_EvtVolume.WB_EvtVolumePartitionRows, {})

            const updateSelectEnabled = () => {
                self._bSalesOrgEnabled = false;
                self._bSalesAreaEnabled = false;
                self.EvtVolumeEnterpriseSelectEnabledChanged.next({
                    SalesOrg: false
                });
            };
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                    updateSelectEnabled();
                });
            } else {
                self._bIsSaved = true;
                updateSelectEnabled();
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {

        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const wbEvtVolume = <IwbLESpend>this.drWB_EvtVolume;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            'Deleting ' + wbEvtVolume.CONTROL_POINT_NAME + '(' + wbEvtVolume.CONTROL_POINT_ID + ')'
        );

        try {
            self.drWB_EvtVolume.markDeleted();
            _.forEach(self.WB_EvtVolume.WB_EvtVolumePartitionRows, (a) => {
                a.markDeleted()
            })

            if (bSaveNow)
                await self.SaveUsing(false, {bSkipValidations: true})

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }
        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: EventVolumeBO = this
        const tgt = new EventVolumeBO({})
        await tgt.LoadNew({
            wbMasterDataSetId: src.drWB_EvtVolume.cif.WB_MDSET_ID,
        })

        if (!tgt.bIsLoaded) {
            throw new Error("Unable to create new Event Volume Config")
        }

        tgt.WB_EvtVolume.cif.CONTROL_POINT_NAME = ''
        RecordUtils.CopyProperties(tgt.dbContext, esWB_EvtVolume,
            src.drWB_EvtVolume, tgt.drWB_EvtVolume,
            [],
            ['WB_SALESORG_ID', 'CONTROL_POINT_ID', 'CONTROL_POINT_NAME', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            {}
        )
        tgt.Identity.setAll(tgt.WB_EvtVolume.cif.WB_EVTVOLUME_ID, tgt.WB_EvtVolume.cif.CONTROL_POINT_ID, tgt.WB_EvtVolume.cif.CONTROL_POINT_NAME)

        // Copy process definitions
        for (let aSrcPartitionRow of src.WB_EvtVolume.WB_EvtVolumePartitionRows) {
            if (!aSrcPartitionRow.isActive) {
                continue
            }
            const newPartitionRow: WB_EvtVolumePartitionRow = await tgt.addPartition(aSrcPartitionRow.cif.WB_PARTITION_ID)
            newPartitionRow.cif.ENABLED_IND = aSrcPartitionRow.cif.ENABLED_IND
            newPartitionRow.cif.MANUAL_IND = aSrcPartitionRow.cif.MANUAL_IND
        }

        tgt._bSalesOrgEnabled = true

        return tgt
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_EvtVolume, opts)
    }


    //</editor-fold>

    //<editor-fold desc = "Partition and Item Operations">

    private _PartitionsChanged: BehaviorSubject<EventVolumeBO>

    private IHavePartitions_init(): void {
        // Create local BehaviorSubjects
        this._PartitionsChanged = new BehaviorSubject<EventVolumeBO>(undefined)
    }


    get PartitionsChanged(): BehaviorSubject<EventVolumeBO> {
        return this._PartitionsChanged
    }

    async addPartition(wbPartitionId?: number): Promise<WB_EvtVolumePartitionRow> {
        const self = this

        let cpPartition
            = <WB_EvtVolumePartitionRow>await self.dbContext.createRecordWithKey(esWB_EvtVolumePR)
        cpPartition.setDefaults()

        cpPartition.cif.WB_EVTVOLUME_ID = self.drWB_EvtVolume.cif.WB_EVTVOLUME_ID
        if (_.isNumber(wbPartitionId))
            cpPartition.cif.WB_PARTITION_ID = wbPartitionId
        cpPartition.cif.SORT_ORDER = self.WB_EvtVolume.WB_EvtVolumePartitionRows.length
        // If partition is added when creating a new control point, the event bindings will happen in the next step. Skip here to avoid repetition
        self.dbContext.add(esWB_EvtVolumePR, cpPartition)
        let DefaultRow = _.first(_.filter(self.WB_EvtVolume.WB_EvtVolumePartitionRows, aRow => aRow.isActive && aRow.cif.WB_PARTITION_ID == -1))
        if (!_.isEmpty(DefaultRow))
            DefaultRow.cif.SORT_ORDER = self.WB_EvtVolume.WB_EvtVolumePartitionRows.length
        self.PartitionsChanged.next(this)
        return cpPartition
    }

    async removePartition(wbEvtVolumePRId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_EvtVolume.WB_EvtVolumePartitionRows, (aRow) => {
            if (!aRow.isActive)
                return

            if (aRow.cif.WB_EVTVOLUMEPR_ID == wbEvtVolumePRId) {
                aRow.markDeleted()
            }
        })

        self.PartitionsChanged.next(this)
    }


    //</editor-fold>


    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const createOptions: CreateOptions = {
            standardConditions: {
                [esWB_EvtVolume]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_EvtVolumePR]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctIncludeBelongsToNamedRelations: { }
        };

        return new ESRecordResolver(dbContext, createOptions);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_EvtVolume, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(EventVolumeBO, "EventVolumeBO", "Event Volume")
